/**
 * Vuex module for the live analysis queue (M1 contract E, task F10).
 *
 * Mirrors `GET /analysis-runs/queue` into three lanes (waiting / running / recent) and keeps them
 * fresh through:
 *   - `startLive()`: an immediate fetch plus a 10 s poll that only ticks while the document is
 *     visible (paused on `visibilitychange` → hidden, resumed and refetched on visible);
 *   - `applyChange(event)`: the `analysis-jobs-changed` socket payload, applied optimistically
 *     (the run moves between lanes) followed by a debounced refetch of the full read model.
 *
 * Control actions (cancel / remove / force / markFailed / reorder / pause / resume) mutate the
 * run optimistically and roll the lanes back when the request fails; the error is rethrown so
 * the view can explain it.
 *
 * M3 additions: `watchRuns(ids)` remembers the runs launched in this session and `applyChange`
 * turns their terminal transitions into `notifications` (App.vue shows them as a snackbar);
 * `applyTick` mirrors the dispatcher's `analysis-queue-tick` {slots, paused, ts} broadcast.
 *
 * Run objects are ledger documents (snake_case, `_id` == run_id). Legacy Node status vocabulary
 * is not translated here: the read model already speaks ledger statuses.
 */
import defaultService from '@/services/AnalysisRunsService';

export const POLL_INTERVAL_MS = 10000;
export const CHANGE_REFETCH_DEBOUNCE_MS = 500;

export const LANES = Object.freeze(['waiting', 'running', 'recent']);

const LANE_BY_STATUS = Object.freeze({
  QUEUED: 'waiting',
  DISPATCHED: 'running',
  RUNNING: 'running',
  CANCEL_REQUESTED: 'running',
  SUCCEEDED: 'recent',
  FAILED: 'recent',
  CANCELLED: 'recent',
});

export const TERMINAL_RUN_STATUSES = Object.freeze(['SUCCEEDED', 'FAILED', 'CANCELLED']);

export function normalizeRunStatus(status) {
  return typeof status === 'string' ? status.trim().toUpperCase() : '';
}

export function laneForStatus(status) {
  return LANE_BY_STATUS[normalizeRunStatus(status)] || null;
}

export function isTerminalRunStatus(status) {
  return TERMINAL_RUN_STATUSES.includes(normalizeRunStatus(status));
}

export function runIdOf(run) {
  if (!run || typeof run !== 'object') return null;
  return run._id || run.run_id || null;
}

/** Upper bound of remembered notifications (oldest dropped first). */
export const MAX_NOTIFICATIONS = 20;

/**
 * Neighbour ids for a move inside the waiting lane (contract 7: PATCH position with
 * `before_run_id` / `after_run_id`). Pure so the drag handler and the tests share it.
 *
 * @param {object[]} waiting current lane order
 * @param {string} runId run being moved
 * @param {number} newIndex target index in the lane after the move
 * @returns {{ order: object[], before_run_id: string|null, after_run_id: string|null }|null}
 *   null when the run is not in the lane or the index does not change anything.
 */
export function planReorder(waiting, runId, newIndex) {
  const runs = toArray(waiting);
  const oldIndex = runs.findIndex((run) => runIdOf(run) === runId);
  if (oldIndex === -1) return null;
  const target = Math.max(0, Math.min(runs.length - 1, Number(newIndex)));
  if (!Number.isFinite(target) || target === oldIndex) return null;
  const order = runs.slice();
  const [moved] = order.splice(oldIndex, 1);
  order.splice(target, 0, moved);
  const before = order[target + 1] || null;
  const after = order[target - 1] || null;
  // Prefer the run we now precede; fall back to the one we follow when moved to the end.
  return {
    order,
    before_run_id: before ? runIdOf(before) : null,
    after_run_id: !before && after ? runIdOf(after) : null,
  };
}

/** Ids accepted by `watchRuns`: strings or objects carrying run_id / _id. */
function normalizeWatchIds(payload) {
  const list = Array.isArray(payload) ? payload : [payload];
  return list
    .map((item) => (typeof item === 'string' ? item : runIdOf(item)))
    .filter((id) => typeof id === 'string' && id.trim())
    .map((id) => id.trim());
}

function defaultSlots() {
  return { capacity: null, in_use: 0, free: null, source: 'unknown' };
}

function defaultHealth() {
  return {
    bridge_alive: null,
    last_message_at: null,
    degraded_runs: 0,
    lost_runs: 0,
    oldest_queued_at: null,
  };
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function cloneLanes(state) {
  return {
    waiting: state.waiting.slice(),
    running: state.running.slice(),
    recent: state.recent.slice(),
  };
}

function findRun(state, runId) {
  for (const lane of LANES) {
    const index = state[lane].findIndex((run) => runIdOf(run) === runId);
    if (index !== -1) return { lane, index, run: state[lane][index] };
  }
  return null;
}

function sortWaiting(runs) {
  // Position first (dispatch order), then enqueue time as a stable fallback.
  return runs.slice().sort((a, b) => {
    const pa = a.position == null ? Number.MAX_SAFE_INTEGER : Number(a.position);
    const pb = b.position == null ? Number.MAX_SAFE_INTEGER : Number(b.position);
    if (pa !== pb) return pa - pb;
    return String(a.enqueued_at || '').localeCompare(String(b.enqueued_at || ''));
  });
}

function sortRecent(runs) {
  return runs
    .slice()
    .sort((a, b) => String(b.finished_at || '').localeCompare(String(a.finished_at || '')));
}

function insertIntoLane(state, lane, run) {
  const runs = state[lane].filter((existing) => runIdOf(existing) !== runIdOf(run));
  runs.push(run);
  if (lane === 'waiting') state.waiting = sortWaiting(runs);
  else if (lane === 'recent') state.recent = sortRecent(runs);
  else state.running = runs;
}

function removeFromLane(state, lane, runId) {
  state[lane] = state[lane].filter((run) => runIdOf(run) !== runId);
}

/** Fields of the `analysis-jobs-changed` payload that are copied onto the cached run. */
function patchFromChange(event, now) {
  const patch = {};
  if (event.status != null) patch.status = normalizeRunStatus(event.status);
  if (event.position !== undefined) patch.position = event.position;
  if (event.progress != null) patch.progress = event.progress;
  if (event.current_step !== undefined) patch.current_step = event.current_step;
  if (event.batch !== undefined) patch.batch = event.batch;
  patch.heartbeat_at = event.ts || now;
  if (patch.status && isTerminalRunStatus(patch.status)) {
    patch.finished_at = event.ts || now;
  }
  return patch;
}

function runFromChange(event, patch) {
  return {
    _id: event.run_id,
    source_type: event.source_type || null,
    source_id: event.source_id != null ? String(event.source_id) : null,
    attempt: null,
    batch: null,
    position: null,
    progress: 0,
    current_step: null,
    engine: null,
    engine_resolved: null,
    engines_requested: [],
    cancel: null,
    heartbeat_at: null,
    enqueued_at: event.ts || null,
    started_at: null,
    finished_at: null,
    ...patch,
  };
}

/**
 * Build the module. Exposed as a factory so tests can inject a fake service and get fresh timers;
 * the default export is the instance registered in the store.
 *
 * @param {{ service?: { queue: Function, cancel: Function, remove: Function, force: Function,
 *   markFailed: Function, pauseQueue?: Function, resumeQueue?: Function, reorder?: Function } }} deps
 */
export function createAnalysisQueueModule({ service = defaultService } = {}) {
  // Non-reactive runtime handles (timers and DOM listener), one set per module instance.
  const runtime = {
    pollTimer: null,
    refetchTimer: null,
    visibilityHandler: null,
  };

  const hasDocument = () => typeof document !== 'undefined';
  const isVisible = () => !hasDocument() || document.visibilityState !== 'hidden';

  function clearPoll() {
    if (runtime.pollTimer) {
      clearInterval(runtime.pollTimer);
      runtime.pollTimer = null;
    }
  }

  function clearRefetch() {
    if (runtime.refetchTimer) {
      clearTimeout(runtime.refetchTimer);
      runtime.refetchTimer = null;
    }
  }

  function ensurePoll(dispatch) {
    if (runtime.pollTimer) return;
    runtime.pollTimer = setInterval(() => {
      if (isVisible()) dispatch('fetchQueue', { silent: true });
    }, POLL_INTERVAL_MS);
  }

  /**
   * Snapshot the lanes, apply an optimistic mutation and run the request; on failure restore the
   * snapshot and rethrow so the caller can surface the error.
   */
  async function optimistic({ state, commit, dispatch }, runId, mutate, request) {
    const snapshot = cloneLanes(state);
    commit('SET_PENDING', { runId, pending: true });
    mutate();
    try {
      const response = await request();
      if (state.live) dispatch('scheduleRefetch');
      return response;
    } catch (error) {
      commit('RESTORE_LANES', snapshot);
      throw error;
    } finally {
      commit('SET_PENDING', { runId, pending: false });
    }
  }

  return {
    namespaced: true,

    state: () => ({
      waiting: [],
      running: [],
      recent: [],
      slots: defaultSlots(),
      health: defaultHealth(),
      versions: { api: null, worker: null },
      paused: false,
      live: false,
      lastUpdatedAt: null,
      loading: false,
      error: null,
      /** run_id -> true while a control request is in flight. */
      pending: {},
      /** Run ids launched from this browser session; their terminal transitions notify. */
      watchedRunIds: [],
      /** Terminal transitions of watched runs, oldest first (see MAX_NOTIFICATIONS). */
      notifications: [],
      /** Last `analysis-queue-tick` broadcast from the dispatcher ({slots, paused, ts}). */
      lastTickAt: null,
    }),

    getters: {
      allRuns: (state) => [...state.waiting, ...state.running, ...state.recent],
      runById: (state, getters) => (runId) =>
        getters.allRuns.find((run) => runIdOf(run) === runId) || null,
      counts: (state) => ({
        waiting: state.waiting.length,
        running: state.running.length,
        recent: state.recent.length,
      }),
      isPending: (state) => (runId) => Boolean(state.pending[runId]),
      isWatched: (state) => (runId) => state.watchedRunIds.includes(runId),
      /** Waiting + running runs of one source (chips on list rows), waiting first. */
      runsForSource: (state) => (sourceType, sourceId) => {
        const id = sourceId == null ? null : String(sourceId);
        if (!id) return [];
        return [...state.waiting, ...state.running].filter(
          (run) =>
            run.source_type === sourceType && run.source_id != null && String(run.source_id) === id
        );
      },
      /** 1-based index in the waiting lane, or null when the run is not waiting. */
      queueIndexOf: (state) => (runId) => {
        const index = state.waiting.findIndex((run) => runIdOf(run) === runId);
        return index === -1 ? null : index + 1;
      },
      nextWaiting: (state) => state.waiting[0] || null,
      latestNotification: (state) =>
        state.notifications.length ? state.notifications[state.notifications.length - 1] : null,
    },

    mutations: {
      SET_LOADING(state, loading) {
        state.loading = Boolean(loading);
      },
      SET_ERROR(state, error) {
        state.error = error || null;
      },
      SET_LIVE(state, live) {
        state.live = Boolean(live);
      },
      SET_QUEUE(state, payload) {
        const data = payload && typeof payload === 'object' ? payload : {};
        state.waiting = sortWaiting(toArray(data.waiting));
        state.running = toArray(data.running);
        state.recent = sortRecent(toArray(data.recent));
        state.slots = { ...defaultSlots(), ...(data.slots || {}) };
        state.health = { ...defaultHealth(), ...(data.health || {}) };
        state.versions = {
          api: data.versions?.api ?? null,
          worker: data.versions?.worker ?? null,
        };
        state.paused = Boolean(data.paused);
        state.lastUpdatedAt = new Date().toISOString();
      },
      /** Move / upsert a run into the lane matching its status; drop it when the lane is unknown. */
      UPSERT_RUN(state, { run, patch = {} }) {
        const runId = runIdOf(run);
        if (!runId) return;
        const found = findRun(state, runId);
        const next = { ...(found ? found.run : run), ...patch };
        const lane = laneForStatus(next.status);
        if (found) removeFromLane(state, found.lane, runId);
        if (lane) insertIntoLane(state, lane, next);
      },
      RESTORE_LANES(state, lanes) {
        state.waiting = lanes.waiting;
        state.running = lanes.running;
        state.recent = lanes.recent;
      },
      SET_PENDING(state, { runId, pending }) {
        const next = { ...state.pending };
        if (pending) next[runId] = true;
        else delete next[runId];
        state.pending = next;
      },
      SET_PAUSED(state, paused) {
        state.paused = Boolean(paused);
      },
      SET_SLOTS(state, slots) {
        state.slots = { ...defaultSlots(), ...(slots || {}) };
      },
      SET_LAST_TICK_AT(state, ts) {
        state.lastTickAt = ts || null;
      },
      SET_WAITING_ORDER(state, runs) {
        state.waiting = toArray(runs);
      },
      ADD_WATCHED(state, ids) {
        const next = new Set(state.watchedRunIds);
        ids.forEach((id) => next.add(id));
        state.watchedRunIds = Array.from(next);
      },
      REMOVE_WATCHED(state, runId) {
        state.watchedRunIds = state.watchedRunIds.filter((id) => id !== runId);
      },
      PUSH_NOTIFICATION(state, notification) {
        const next = state.notifications.concat([notification]);
        state.notifications =
          next.length > MAX_NOTIFICATIONS ? next.slice(next.length - MAX_NOTIFICATIONS) : next;
      },
      DISMISS_NOTIFICATION(state, runId) {
        state.notifications = state.notifications.filter((item) => item.run_id !== runId);
      },
      CLEAR_NOTIFICATIONS(state) {
        state.notifications = [];
      },
    },

    actions: {
      /**
       * Load the queue read model. `silent` keeps the current data on screen (no loading flag)
       * for background refreshes.
       */
      async fetchQueue({ commit }, { silent = false } = {}) {
        if (!silent) commit('SET_LOADING', true);
        try {
          const response = await service.queue();
          commit('SET_QUEUE', response?.data);
          commit('SET_ERROR', null);
        } catch (error) {
          const data = error?.response?.data;
          commit(
            'SET_ERROR',
            data?.long || data?.short || data?.message || 'Could not load the analysis queue.'
          );
          throw error;
        } finally {
          if (!silent) commit('SET_LOADING', false);
        }
      },

      /** Fetch now and poll every 10 s while the tab is visible. Idempotent. */
      async startLive({ state, commit, dispatch }) {
        if (!state.live) {
          commit('SET_LIVE', true);
          if (hasDocument() && !runtime.visibilityHandler) {
            runtime.visibilityHandler = () => {
              if (isVisible()) {
                dispatch('fetchQueue', { silent: true }).catch(() => {});
                ensurePoll(dispatch);
              } else {
                clearPoll();
              }
            };
            document.addEventListener('visibilitychange', runtime.visibilityHandler);
          }
        }
        if (isVisible()) ensurePoll(dispatch);
        try {
          await dispatch('fetchQueue');
        } catch (_error) {
          // Surfaced through state.error; polling keeps retrying.
        }
      },

      stopLive({ commit }) {
        clearPoll();
        clearRefetch();
        if (runtime.visibilityHandler && hasDocument()) {
          document.removeEventListener('visibilitychange', runtime.visibilityHandler);
        }
        runtime.visibilityHandler = null;
        commit('SET_LIVE', false);
      },

      /** Debounced full refresh after socket changes or control actions. */
      scheduleRefetch({ dispatch }) {
        clearRefetch();
        runtime.refetchTimer = setTimeout(() => {
          runtime.refetchTimer = null;
          dispatch('fetchQueue', { silent: true }).catch(() => {});
        }, CHANGE_REFETCH_DEBOUNCE_MS);
      },

      /**
       * `analysis-jobs-changed` payload: {run_id, source_type, source_id, status, position,
       * progress, current_step, batch, ts}. Moves the run between lanes at once and refreshes the
       * full read model shortly after (only while live, to avoid background traffic).
       */
      applyChange({ state, commit, dispatch }, event) {
        const runId = event && (event.run_id || event._id);
        if (!runId) return;
        const now = new Date().toISOString();
        const patch = patchFromChange(event, now);
        const found = findRun(state, runId);
        if (found) {
          commit('UPSERT_RUN', { run: found.run, patch });
        } else if (patch.status && !isTerminalRunStatus(patch.status)) {
          // A run we have not seen yet (enqueued elsewhere): show a stub until the refetch fills it.
          commit('UPSERT_RUN', { run: runFromChange({ ...event, run_id: runId }, patch) });
        }
        if (
          patch.status &&
          isTerminalRunStatus(patch.status) &&
          state.watchedRunIds.includes(runId)
        ) {
          const cached = found ? found.run : null;
          commit('PUSH_NOTIFICATION', {
            run_id: runId,
            status: patch.status,
            source_type: event.source_type || cached?.source_type || null,
            source_id:
              event.source_id != null
                ? String(event.source_id)
                : cached?.source_id != null
                  ? String(cached.source_id)
                  : null,
            source_label: event.source_label || cached?.source_label || null,
            batch: event.batch !== undefined ? event.batch : (cached?.batch ?? null),
            ts: event.ts || now,
          });
          commit('REMOVE_WATCHED', runId);
        }
        if (state.live) dispatch('scheduleRefetch');
      },

      /**
       * Remember runs launched from this session (LaunchAnalysisDialog result) so their terminal
       * transitions produce a notification. Accepts run ids or objects with run_id / _id.
       */
      watchRuns({ commit }, payload) {
        const ids = normalizeWatchIds(payload);
        if (ids.length) commit('ADD_WATCHED', ids);
      },

      /** Alias of `watchRuns` (F22 contract name). */
      addWatchedRuns({ dispatch }, payload) {
        return dispatch('watchRuns', payload);
      },

      unwatchRun({ commit }, runId) {
        if (runId) commit('REMOVE_WATCHED', runId);
      },

      dismissNotification({ commit }, runId) {
        if (runId) commit('DISMISS_NOTIFICATION', runId);
        else commit('CLEAR_NOTIFICATIONS');
      },

      /** `analysis-queue-tick` broadcast: {slots, paused, ts} from the dispatcher. */
      applyTick({ commit }, payload) {
        if (!payload || typeof payload !== 'object') return;
        if (payload.slots && typeof payload.slots === 'object') commit('SET_SLOTS', payload.slots);
        if (typeof payload.paused === 'boolean') commit('SET_PAUSED', payload.paused);
        commit('SET_LAST_TICK_AT', payload.ts || new Date().toISOString());
      },

      /** Pause the holding pen: queued runs are not dispatched until `resume`. */
      async pause({ state, commit, dispatch }) {
        const previous = state.paused;
        commit('SET_PAUSED', true);
        try {
          const response = await service.pauseQueue();
          if (state.live) dispatch('scheduleRefetch');
          return response;
        } catch (error) {
          commit('SET_PAUSED', previous);
          throw error;
        }
      },

      async resume({ state, commit, dispatch }) {
        const previous = state.paused;
        commit('SET_PAUSED', false);
        try {
          const response = await service.resumeQueue();
          if (state.live) dispatch('scheduleRefetch');
          return response;
        } catch (error) {
          commit('SET_PAUSED', previous);
          throw error;
        }
      },

      /**
       * Move a waiting run to `newIndex` in the lane (drag and drop). Optimistic reorder; the
       * server answers with the new gap-numbered position and the refetch settles the lane.
       * Resolves false when nothing changes.
       */
      reorder(context, { runId, newIndex }) {
        const { state, commit } = context;
        const plan = planReorder(state.waiting, runId, newIndex);
        if (!plan) return Promise.resolve(false);
        const body = plan.before_run_id
          ? { before_run_id: plan.before_run_id }
          : { after_run_id: plan.after_run_id };
        return optimistic(
          context,
          runId,
          () => commit('SET_WAITING_ORDER', plan.order),
          () => service.reorder(runId, body)
        );
      },

      /** RUNNING -> CANCEL_REQUESTED (cooperative). 409 when the worker is already persisting. */
      cancel(context, runId) {
        const { state, commit } = context;
        return optimistic(
          context,
          runId,
          () => {
            const found = findRun(state, runId);
            if (!found) return;
            commit('UPSERT_RUN', {
              run: found.run,
              patch: {
                status: 'CANCEL_REQUESTED',
                cancel: {
                  requested_at: new Date().toISOString(),
                  requested_by: null,
                  reason: null,
                  forced: false,
                  ignored_because_finishing: false,
                  ...(found.run.cancel || {}),
                },
              },
            });
          },
          () => service.cancel(runId)
        );
      },

      /** QUEUED | DISPATCHED -> CANCELLED before the worker starts. */
      remove(context, runId) {
        const { state, commit } = context;
        return optimistic(
          context,
          runId,
          () => {
            const found = findRun(state, runId);
            if (!found) return;
            commit('UPSERT_RUN', {
              run: found.run,
              patch: { status: 'CANCELLED', finished_at: new Date().toISOString() },
            });
          },
          () => service.remove(runId)
        );
      },

      /** Force-terminate a CANCEL_REQUESTED run (server flag + grace or explicit confirm). */
      force(context, { runId, confirm = false }) {
        const { state, commit } = context;
        return optimistic(
          context,
          runId,
          () => {
            const found = findRun(state, runId);
            if (!found) return;
            commit('UPSERT_RUN', {
              run: found.run,
              patch: { cancel: { ...(found.run.cancel || {}), forced: true } },
            });
          },
          () => service.force(runId, confirm)
        );
      },

      /** RUNNING | CANCEL_REQUESTED with a stale heartbeat -> FAILED (worker_lost). */
      markFailed(context, runId) {
        const { state, commit } = context;
        return optimistic(
          context,
          runId,
          () => {
            const found = findRun(state, runId);
            if (!found) return;
            commit('UPSERT_RUN', {
              run: found.run,
              patch: {
                status: 'FAILED',
                finished_at: new Date().toISOString(),
                error: { type: 'worker_lost', message: 'Marked as failed by user', stage: null },
              },
            });
          },
          () => service.markFailed(runId)
        );
      },
    },
  };
}

export default createAnalysisQueueModule();
